import { IDispatchAction } from 'models/dispatch-action.interface';
import { IProfileAsset } from 'models/profile/i-profile-asset.interface';
import { ProfileAssetOrderingEnum } from 'models/profile/i-profile-assets-filter.interface';
import { IWalletReducer } from 'models/reducers/i-wallet-reducer.interface';

import {
  CANCEL_BID,
  FETCH_WALLET_CURRENCIES,
  FETCH_WALLET_CURRENCIES_WITH_BALANCE,
  REMOVE_WALLET_LISTING,
  RESET_ASSETS,
  RESET_LISTINGS,
  RESET_OFFERS,
  SET_NEW_NFTS,
  SET_PROFILE_ASSETS_FILTERS,
  SET_REFERRER_WALLET,
  SET_WALLET_ADDRESS,
  SET_ALL_WALLET_ASSETS,
  SET_ALL_WALLET_LISTINGS,
  SET_EMPIRE_ASSETS,
  SET_WALLET_ASSETS,
  SET_WALLET_LISTINGS,
  SET_WALLET_OFFERS,
  WALLET_SIGN_OUT,
} from 'constants/actionNames';

import { CANCEL_LISTING } from 'actions/actionNames/venuesActionNames';

const CANCELED_ERROR_MESSAGE = 'canceled';

const INITIAL_STATE: IWalletReducer = {
  address: '',
  referrerWallet: null,
  assets: null,
  assetsFetchedAt: null,
  assetsLoading: false,
  assetsError: '',
  assetsLastPage: false,
  assetsPendingSearch: null,
  assetsLastSearch: null,
  empireAssets: null,
  empireAssetsFetchedAt: null,
  empireAssetsLoading: false,
  empireAssetsError: '',
  offersAssets: null,
  offersAssetsLoading: false,
  offersAssetsError: '',
  offersAssetsLastPage: false,
  listingsAssets: null,
  listingsAssetsLoading: false,
  listingsAssetsError: '',
  listingsAssetsFetchedAt: null,
  listingsAssetsLastPage: false,
  currencies: null,
  currenciesLoading: false,
  currenciesError: '',
  newNfts: 0,
  filters: {
    search: '',
    ordering: ProfileAssetOrderingEnum.newestFirst,
    country: '',
    category: '',
    created_by_me: '',
  },
};

export const walletReducer = (state = INITIAL_STATE, action: IDispatchAction<any>): IWalletReducer => {
  let loading = false;

  switch (action.type) {
    case WALLET_SIGN_OUT + '_FULFILLED':
      // TODO: move currencies to own reducer
      return {
        ...INITIAL_STATE,
        currencies: state.currencies,
        referrerWallet: state.referrerWallet, // Keep referrerWallet on sign-out
      };
    case RESET_ASSETS:
      return { ...state, assets: null, assetsFetchedAt: null, assetsLastPage: false };
    case RESET_OFFERS:
      return { ...state, offersAssets: null, offersAssetsLastPage: false };
    case RESET_LISTINGS:
      return { ...state, listingsAssets: null, listingsAssetsFetchedAt: null, listingsAssetsLastPage: false };
    case SET_WALLET_ADDRESS:
      return { ...state, address: action.payload };
    case SET_REFERRER_WALLET:
      return { ...state, referrerWallet: action.payload };
    case SET_PROFILE_ASSETS_FILTERS:
      return { ...state, filters: action.payload };

    // collection
    case SET_ALL_WALLET_ASSETS + '_PENDING':
      return { ...state, assetsLoading: true, assetsError: '', assetsPendingSearch: (action.meta?.search as string) ?? null };
    case SET_ALL_WALLET_ASSETS + '_REJECTED':
      return { ...state, assetsLoading: false, assetsError: 'Api issue', assetsPendingSearch: null };
    case SET_ALL_WALLET_ASSETS + '_FULFILLED': {
      const fulfilledSearch = (action.meta?.search as string) ?? '';
      const isUnfilteredSnapshot = fulfilledSearch === '';
      return {
        ...state,
        assets: action.payload.data.results,
        assetsFetchedAt: Date.now(),
        assetsLoading: false,
        assetsError: '',
        assetsLastPage: true,
        assetsPendingSearch: null,
        assetsLastSearch: fulfilledSearch,
        ...(isUnfilteredSnapshot && {
          empireAssets: action.payload.data.results,
          empireAssetsFetchedAt: Date.now(),
          empireAssetsLoading: false,
          empireAssetsError: '',
        }),
      };
    }

    // empire / fNFT Holdings summary — unfiltered owned assets, separate from the
    // search-filtered collected grid (state.assets) so the summary is view- and
    // filter-independent.
    case SET_EMPIRE_ASSETS + '_PENDING':
      return { ...state, empireAssetsLoading: true, empireAssetsError: '' };
    case SET_EMPIRE_ASSETS + '_REJECTED':
      return { ...state, empireAssetsLoading: false, empireAssetsError: 'Api issue' };
    case SET_EMPIRE_ASSETS + '_FULFILLED':
      return {
        ...state,
        empireAssets: action.payload.data.results,
        empireAssetsFetchedAt: Date.now(),
        empireAssetsLoading: false,
        empireAssetsError: '',
      };

    case SET_ALL_WALLET_LISTINGS + '_PENDING':
      return { ...state, listingsAssetsLoading: true, listingsAssetsError: '' };
    case SET_ALL_WALLET_LISTINGS + '_REJECTED':
      return { ...state, listingsAssetsLoading: false, listingsAssetsError: 'Api issue' };
    case SET_ALL_WALLET_LISTINGS + '_FULFILLED':
      return {
        ...state,
        listingsAssets: action.payload.data.results,
        listingsAssetsFetchedAt: Date.now(),
        listingsAssetsLoading: false,
        listingsAssetsError: '',
        listingsAssetsLastPage: true,
      };

    case SET_WALLET_ASSETS + '_PENDING':
      return { ...state, assetsLoading: true, assetsError: '' };
    case SET_WALLET_ASSETS + '_REJECTED':
      if (action.payload?.message?.toLowerCase() === CANCELED_ERROR_MESSAGE) {
        loading = true;
      }
      return { ...state, assetsLoading: loading, assetsError: 'Api issue' };
    case SET_WALLET_ASSETS + '_FULFILLED':
      const results = action.payload.data.results;
      const assets = state.assets ?? [];

      return {
        ...state,
        assets: [...assets.filter(({ id }) => !results.some((asset: IProfileAsset) => asset.id === id)), ...results],
        assetsLoading: false,
        assetsError: '',
        assetsLastPage: !action.payload.data.next,
      };

    // offers
    case SET_WALLET_OFFERS + '_PENDING':
      return { ...state, offersAssetsLoading: true, offersAssetsError: '' };
    case SET_WALLET_OFFERS + '_REJECTED':
      if (action.payload?.message?.toLowerCase() === CANCELED_ERROR_MESSAGE) {
        loading = true;
      }
      return { ...state, offersAssetsLoading: loading, offersAssetsError: 'Api issue' };
    case SET_WALLET_OFFERS + '_FULFILLED':
      return {
        ...state,
        offersAssets: [...(state.offersAssets ?? []), ...action.payload.data.results],
        offersAssetsLoading: false,
        offersAssetsError: '',
        offersAssetsLastPage: !action.payload.data.next,
      };
    case CANCEL_BID + '_FULFILLED':
      return {
        ...state,
        offersAssets: state.offersAssets?.filter((offer) => offer.id !== action.payload.data)! ?? state.offersAssets,
      };

    // listing
    case SET_WALLET_LISTINGS + '_PENDING':
      return { ...state, listingsAssetsLoading: true, listingsAssetsError: '' };
    case SET_WALLET_LISTINGS + '_REJECTED':
      if (action.payload?.message?.toLowerCase() === CANCELED_ERROR_MESSAGE) {
        loading = true;
      }
      return { ...state, listingsAssetsLoading: loading, listingsAssetsError: 'Api issue' };
    case SET_WALLET_LISTINGS + '_FULFILLED':
      return {
        ...state,
        listingsAssets: [...(state.listingsAssets ?? []), ...action.payload.data.results],
        listingsAssetsLoading: false,
        listingsAssetsError: '',
        listingsAssetsLastPage: !action.payload.data.next,
      };
    case CANCEL_LISTING + '_FULFILLED': {
      if (!state.listingsAssets) {
        return state;
      }

      return {
        ...state,
        listingsAssets: state.listingsAssets.filter((listing) => listing.id !== action.payload),
      };
    }
    case REMOVE_WALLET_LISTING: {
      return {
        ...state,
        listingsAssets:
          state.listingsAssets?.filter((listing) => listing.id !== action.payload) ?? state.listingsAssets,
      };
    }

    // fetch currencies
    case FETCH_WALLET_CURRENCIES + '_PENDING':
      return { ...state, currenciesLoading: true, currenciesError: '' };
    case FETCH_WALLET_CURRENCIES + '_REJECTED':
      return { ...state, currenciesLoading: false, currenciesError: 'Api issue' };
    case FETCH_WALLET_CURRENCIES + '_FULFILLED':
      return {
        ...state,
        currencies: action.payload.data,
        currenciesLoading: false,
        currenciesError: '',
      };

    // fetch currencies with balance
    case FETCH_WALLET_CURRENCIES_WITH_BALANCE + '_PENDING':
      return { ...state, currenciesLoading: true, currenciesError: '' };
    case FETCH_WALLET_CURRENCIES_WITH_BALANCE + '_REJECTED':
      return { ...state, currenciesLoading: false, currenciesError: 'Api issue' };
    case SET_NEW_NFTS:
      return { ...state, newNfts: action.payload };
    default:
      return state;
  }
};
